import Analytics from '@/utils/analytics';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActionToastContent } from '@/components/action-toast-content';
import { DEFAULT_CURRENCY_CODE } from '@/utils/money';
import {
  FilteredCategoryItemGroup,
  ItemListOption,
  ItemListOptionSection,
  OrderGuideCollection,
  OrderGuidePartition,
  OrderGuideProviderProps,
  PartitionFilterType,
  VariantPackGroup,
  VariantPackPartitionMap,
} from './item-context-types';
import { getVariantPackGroupParentUUIDs } from '@/utils/category';
import { gql, useMutation } from '@apollo/client';
import {
  groupItemListOptions,
  useAdaptVariantPackToItemListOption,
} from './item-context-utils';
import {
  ItemsContext_CreateDefaultOrderGuideRecordsMutation,
  ItemsContext_CreateDefaultOrderGuideRecordsMutationVariables,
  ItemsContext_MultiOrderGuideItemsQuery,
  ItemsContext_MultiOrderGuideItemsQueryVariables,
  ItemsContext_OrderGuideItemFieldsFragment,
  ItemsContext_OrderGuideRestaurantCollectionFieldsFragment,
  ItemsContext_OrderGuideSupplierCollectionFieldsFragment,
  ItemsContext_VariantsPackFieldsFragment,
} from '@/types';
import { MULTI_ORDER_GUIDE_QUERY } from './items-context-queries';
import {
  ORDER_GUIDE_SETTINGS_KEY,
  OrderGuideSettingsType,
  useOrderGuideSettingsContext,
} from './order-guide-settings-context';
import { OrderGuideItemsContext } from './order-guide-items-context';
import { OrderGuideMergeStrategy } from '@/enums';
import {
  selectIsInOrderGuide,
  useOrderGuideStore,
  useOrderGuideStoreApi,
} from '@/stores/order-guide-store';
import { sortItemListOptions } from '@/utils/item';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemDisplayOptions, useNewOrderContext } from './new-order-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineOrderGuide } from '@/hooks/use-offline-data';
import { useOrderGuideItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useToasts } from './toast-context';
import { useUserContext } from './user-context';
import { useVariantPackInfoContext } from './variant-pack-info-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const NO_COLLECTION_NAME = i18n.t('order_guide');
export const NO_PARTITION_NAME = i18n.t('default');

export const CREATE_DEFAULT_ORDER_GUIDE_RECORDS_MUTATION = gql`
  mutation ItemsContext_CreateDefaultOrderGuideRecords(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    createDefaultOrderGuideRecords(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      collection_id
      partition_id
    }
  }
`;

// Manually sort in js instead of using hasura to allow client to update order
// without having to refetch context data
const isSupplierLinkedCollection = (
  c:
    | ItemsContext_OrderGuideRestaurantCollectionFieldsFragment
    | ItemsContext_OrderGuideSupplierCollectionFieldsFragment,
) =>
  c.__typename === 'order_guide_supplier_collection' ||
  Boolean(
    'linked_order_guide_supplier_collection_uuid' in c &&
      c.linked_order_guide_supplier_collection_uuid,
  );

export const sortCollections = (
  a:
    | ItemsContext_OrderGuideRestaurantCollectionFieldsFragment
    | ItemsContext_OrderGuideSupplierCollectionFieldsFragment,
  b:
    | ItemsContext_OrderGuideRestaurantCollectionFieldsFragment
    | ItemsContext_OrderGuideSupplierCollectionFieldsFragment,
) => {
  // Sort by explicit sort order, then by supplier-linked collections before
  // unlinked restaurant collections, then by non-default collections before
  // default collections, and finally alphabetically by name
  if (typeof a.sort_order === 'number' && typeof b.sort_order === 'number') {
    return a.sort_order - b.sort_order;
  } else if (typeof a.sort_order === 'number') {
    return -1;
  } else if (typeof b.sort_order === 'number') {
    return 1;
  } else if (isSupplierLinkedCollection(a) && !isSupplierLinkedCollection(b)) {
    return -1;
  } else if (!isSupplierLinkedCollection(a) && isSupplierLinkedCollection(b)) {
    return 1;
  } else if (a.is_default) {
    return 1;
  } else if (b.is_default) {
    return -1;
  } else {
    // these only are missing a name if they are default, but guard here
    // to be safe and appease typescript
    if (!a.name || !b.name) {
      return 0;
    }
    return a.name.localeCompare(b.name);
  }
};

export const buildPartitionToCollectionMap = (
  collections: { partitions: { uuid: string }[]; uuid: string }[],
) => {
  const map = new Map<string, string>();
  collections.forEach((collection) => {
    collection.partitions.forEach((partition) => {
      map.set(partition.uuid, collection.uuid);
    });
  });
  return map;
};

export const isItemInSelectedCollection = (
  partitionUUID: string | null | undefined,
  selectedCollectionPartitions: Set<string>,
  selectedCollection: OrderGuideCollection | null | undefined,
  supplierOrderGuideCollectionsMap: {
    [uuid: string]: ItemsContext_OrderGuideSupplierCollectionFieldsFragment;
  },
): boolean => {
  let result = partitionUUID
    ? selectedCollectionPartitions.has(partitionUUID)
    : (selectedCollection?.isDefault ?? false);

  // Check if the item is in the linked supplier collection, since the selected
  // collection may be the OGRC that takes precedence over the OGSC
  if (selectedCollection?.linkedSupplierCollectionUUID) {
    const linkedSupplierPartitionUUID = supplierOrderGuideCollectionsMap[
      selectedCollection.linkedSupplierCollectionUUID
    ]?.order_guide_supplier_partitions.find((ogsp) => ogsp.is_default)?.uuid;
    if (partitionUUID === linkedSupplierPartitionUUID) {
      result = true;
    }
  }

  return result;
};

export const MultiOrderGuideItemsContextProvider: React.FC<
  OrderGuideProviderProps
> = ({ children }) => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const itemDisplayOptions = useItemDisplayOptions();
  const itemQueryVariables = useOrderGuideItemQueryVariables();
  const { showErrorToast, showToast } = useToasts();
  const { selectedRestaurantRelationship, selectedRestaurantUUID } =
    useUserContext();
  const {
    orderGuideSortOrder,
    selectedCollectionUUID,
    setSelectedCollectionUUID,
    setShowCategoryFilters,
    showCategoryFilters,
  } = useOrderGuideSettingsContext();
  const {
    childVariantPackGroupMap,
    parentVariantPackGroupMap,
    variantPackGroupMap,
  } = useVariantPackInfoContext();
  const { orderGuideMergeStrategy } = useNewOrderContext();
  const orderGuideStoreApi = useOrderGuideStoreApi();

  const [partitionFilter, setPartitionFilter] =
    useState<PartitionFilterType | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [categoryItemGroupFilter, setCategoryItemGroupFilter] =
    useState<FilteredCategoryItemGroup | null>(null);

  const offlineOrderGuide = useOfflineOrderGuide(
    selectedRestaurantUUID,
    selectedRestaurantRelationship.supplierUUID,
  );

  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const isOfflineModeActive = useOfflineModeActive();
  const query = useWrappedQuery<
    ItemsContext_MultiOrderGuideItemsQuery,
    ItemsContext_MultiOrderGuideItemsQueryVariables
  >(MULTI_ORDER_GUIDE_QUERY, {
    context: {
      noBatch: true,
      performance: {
        parentSpanName: 'Span::order_guide::load',
        spanName: 'Span::order_guide::data_fetch',
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: isOfflineModeActive,
    variables: {
      ...itemQueryVariables,
      restaurantUUID: selectedRestaurantUUID,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    },
  });

  const queryResult = useMemo(() => {
    if (isOfflineModeActive && offlineOrderGuide.data) {
      return offlineOrderGuide;
    }
    return query;
  }, [isOfflineModeActive, offlineOrderGuide, query]);

  // Subscribe to store state (restaurant collections are modifiable, so stored in zustand)
  const storeOrderGuideItems = useOrderGuideStore(
    (state) => state.orderGuideItems,
  );
  const storeRestaurantCollections = useOrderGuideStore(
    (state) => state.restaurantCollections,
  );
  const storeIsHydrated = useOrderGuideStore((state) => state.isHydrated);

  // Hydrate store when query data arrives
  useEffect(() => {
    if (queryResult.data) {
      orderGuideStoreApi.getState().hydrate(queryResult.data);
    }
  }, [orderGuideStoreApi, queryResult.data, selectedRestaurantUUID]);

  const isCreatingDefaultsRef = useRef(false);
  const [createDefaultOrderGuideRecords] = useMutation<
    ItemsContext_CreateDefaultOrderGuideRecordsMutation,
    ItemsContext_CreateDefaultOrderGuideRecordsMutationVariables
  >(CREATE_DEFAULT_ORDER_GUIDE_RECORDS_MUTATION);

  useEffect(() => {
    if (queryResult.error && !isOfflineModeActive) {
      showErrorToast();
    }
  }, [isOfflineModeActive, queryResult.error, showErrorToast]);

  const setInitialCategoryFilter = useCallback(
    async (nextValue: boolean) => {
      try {
        const savedOrderGuideSettingsString = await AsyncStorage.getItem(
          ORDER_GUIDE_SETTINGS_KEY,
        );
        if (savedOrderGuideSettingsString) {
          const savedOrderGuideSettings: Partial<OrderGuideSettingsType> =
            JSON.parse(savedOrderGuideSettingsString);
          if (
            typeof savedOrderGuideSettings?.showCategoryFilters !== 'boolean'
          ) {
            setShowCategoryFilters(nextValue);
          }
        } else {
          setShowCategoryFilters(nextValue);
        }
      } catch {
        // async storage error is not actionable
      }
    },
    [setShowCategoryFilters],
  );

  const supplierOrderGuideCollectionsMap = useMemo(() => {
    return (queryResult.data?.order_guide_supplier_collection || []).reduce<{
      [uuid: string]: ItemsContext_OrderGuideSupplierCollectionFieldsFragment;
    }>((acc, ogsc) => {
      acc[ogsc.uuid] = ogsc;
      return acc;
    }, {});
  }, [queryResult.data?.order_guide_supplier_collection]);

  const collections = useMemo<OrderGuideCollection[]>(() => {
    const linkedSupplierCollectionUUIDSet = new Set<string>();

    const orderGuideCollections: (
      | ItemsContext_OrderGuideRestaurantCollectionFieldsFragment
      | ItemsContext_OrderGuideSupplierCollectionFieldsFragment
    )[] = [];

    // DISTRIBUTOR_ONLY merge strategy should not include OGRCs, only OGSCs
    if (orderGuideMergeStrategy !== OrderGuideMergeStrategy.DISTRIBUTOR_ONLY) {
      for (const ogrc of storeRestaurantCollections) {
        orderGuideCollections.push(ogrc);

        // Remove the linked supplier collection from the map
        if (ogrc.linked_order_guide_supplier_collection_uuid) {
          linkedSupplierCollectionUUIDSet.add(
            ogrc.linked_order_guide_supplier_collection_uuid,
          );
        }
      }
    }

    // Append any remaining supplier collections
    if (orderGuideMergeStrategy !== OrderGuideMergeStrategy.OPERATOR_ONLY) {
      for (const ogsc of Object.values(supplierOrderGuideCollectionsMap)) {
        if (!linkedSupplierCollectionUUIDSet.has(ogsc.uuid)) {
          orderGuideCollections.push(ogsc);
        }
      }
    }

    return orderGuideCollections.sort(sortCollections).map((ogc) => {
      const isRestaurantCollection =
        ogc.__typename === 'order_guide_restaurant_collection';

      const isRestaurantCollectionLocked =
        isRestaurantCollection &&
        ogc.is_default &&
        orderGuideMergeStrategy === OrderGuideMergeStrategy.DISTRIBUTOR_LOCKED;

      let defaultPartitionUUID: string | null = null;
      const partitions = (
        'order_guide_restaurant_partitions' in ogc
          ? ogc.order_guide_restaurant_partitions
          : ogc.order_guide_supplier_partitions
      )
        .reduce<OrderGuidePartition[]>((acc, partition) => {
          if (partition.is_default) {
            defaultPartitionUUID = partition.uuid;
          }

          // Only include the default partition if the collection is locked,
          // otherwise include all partitions
          if (partition.is_default || !isRestaurantCollectionLocked) {
            acc.push({
              isDefault: partition.is_default,
              name: partition.name || NO_PARTITION_NAME,
              uuid: partition.uuid,
            });
          }

          return acc;
        }, [])
        .sort((a, b) => {
          if (a.isDefault) {
            return 1;
          }
          if (b.isDefault) {
            return -1;
          }
          return 0;
        });

      return {
        defaultPartitionUUID,
        isDefault: ogc.is_default,
        isLocked: isRestaurantCollectionLocked,
        name: ogc.name || NO_COLLECTION_NAME,
        partitions,
        uuid: ogc.uuid,
        ...(isRestaurantCollection
          ? {
              linkedSupplierCollectionUUID:
                ogc.linked_order_guide_supplier_collection_uuid || null,
              type: 'restaurant',
            }
          : { type: 'supplier' }),
      };
    });
  }, [
    orderGuideMergeStrategy,
    storeRestaurantCollections,
    supplierOrderGuideCollectionsMap,
  ]);

  const selectedCollection = useMemo<OrderGuideCollection | null>(() => {
    if (!storeIsHydrated) {
      return null;
    }
    let foundSelectedCollection = collections.find(
      (ogc) => ogc.uuid === selectedCollectionUUID,
    );
    const defaultCollection = collections.find(
      (collection) => collection.isDefault,
    );
    if (!foundSelectedCollection) {
      // If the selected collection is not found, try to find the linked restaurant collection,
      // otherwise use the default collection
      foundSelectedCollection =
        collections.find(
          (ogc) => ogc.linkedSupplierCollectionUUID === selectedCollectionUUID,
        ) || defaultCollection;
      if (!foundSelectedCollection) {
        return null;
      }

      // Update selected collection if previous one was not found
      setSelectedCollectionUUID(foundSelectedCollection.uuid);
    }
    const hasNonDefaultPartition = (defaultCollection?.partitions || []).some(
      (partition) => !partition.isDefault,
    );

    setInitialCategoryFilter(!hasNonDefaultPartition);

    return foundSelectedCollection;
    // Don't rerun this hook if setInitialCategoryFilter to safe guard unrelated
    // changes to the settings context from continuously resetting changing
    // the category filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    collections,
    storeIsHydrated,
    selectedCollectionUUID,
    setSelectedCollectionUUID,
  ]);

  useEffect(() => {
    const defaultCollection = storeRestaurantCollections.find(
      (ogrc) => ogrc.is_default,
    );
    const hasDefaultCollectionAndPartition =
      defaultCollection &&
      defaultCollection.order_guide_restaurant_partitions.find(
        (x) => x.is_default,
      );
    const refetchOrderGuideItems = queryResult.refetch;
    const restaurantUUID = selectedRestaurantRelationship.uuid;

    if (
      storeIsHydrated &&
      !hasDefaultCollectionAndPartition &&
      !isCreatingDefaultsRef.current &&
      !isOfflineModeActive
    ) {
      (async () => {
        // Ensure the mutation is never executed more than once
        isCreatingDefaultsRef.current = true;

        const createAndRefetch = async () => {
          try {
            await createDefaultOrderGuideRecords({
              variables: {
                restaurantUUID,
                supplierUUID: selectedRestaurantRelationship.supplierUUID,
              },
            });

            // Refetch to sync default collection and partition
            await refetchOrderGuideItems();
          } catch (e) {
            showToast({
              autoCloseTimeout: 0,
              content: (onClose) => (
                <ActionToastContent
                  buttonProps={{
                    children: i18n.t('retry'),
                    onPress: async () => {
                      onClose();
                      await createAndRefetch();
                    },
                    trackingData: {
                      name: 'multi_order_guide.order_guide_defaults_retry_button.press',
                      properties: { restaurantUUID },
                    },
                  }}
                  textContent={i18n.t(
                    'toast.create_order_guide_defaults_error',
                  )}
                />
              ),
            });
          }
        };

        Analytics.track('multi_order_guide.order_guide_defaults.create', {
          restaurantUUID,
        });

        await createAndRefetch();
      })();
    }
  }, [
    createDefaultOrderGuideRecords,
    isOfflineModeActive,
    storeIsHydrated,
    storeRestaurantCollections,
    queryResult.refetch,
    selectedRestaurantRelationship,
    showToast,
  ]);

  const allVariantPacks = useMemo(() => {
    return Object.values(
      storeOrderGuideItems.reduce<{
        [uuid: string]: ItemsContext_VariantsPackFieldsFragment;
      }>((acc, { variant_pack }) => {
        if (variant_pack && !acc[variant_pack.uuid]) {
          acc[variant_pack.uuid] = variant_pack;
        }
        return acc;
      }, {}),
    );
  }, [storeOrderGuideItems]);

  const categories = useMemo<string[]>(() => {
    const selectedCollectionPartitions = new Set(
      (selectedCollection?.partitions || []).map((partition) => partition.uuid),
    );

    const uniqueCategories = storeOrderGuideItems.reduce<Set<string>>(
      (acc, og) => {
        const category =
          og.variant_pack?.item.restaurant_item_overrides[0]?.category ||
          og.variant_pack?.item.category ||
          '';

        const partitionUUID = og.order_guide_item_partition_id;
        const isInSelectedCollection = isItemInSelectedCollection(
          partitionUUID,
          selectedCollectionPartitions,
          selectedCollection,
          supplierOrderGuideCollectionsMap,
        );

        if (category && isInSelectedCollection) {
          acc.add(category);
        }

        return acc;
      },
      new Set(),
    );

    return uniqueCategories.size > 1 ? Array.from(uniqueCategories).sort() : [];
  }, [
    selectedCollection,
    storeOrderGuideItems,
    supplierOrderGuideCollectionsMap,
  ]);

  const categoryItemGroups = useMemo<VariantPackGroup[]>(() => {
    const selectedCollectionPartitions = new Set(
      (selectedCollection?.partitions || []).map((partition) => partition.uuid),
    );

    const itemGroups = storeOrderGuideItems.reduce<Set<VariantPackGroup>>(
      (acc, og) => {
        const partitionUUID = og.order_guide_item_partition_id;
        const isInSelectedCollection = isItemInSelectedCollection(
          partitionUUID,
          selectedCollectionPartitions,
          selectedCollection,
          supplierOrderGuideCollectionsMap,
        );

        const ogItemGroupUUIDs = getVariantPackGroupParentUUIDs(
          (og.variant_pack?.variant_pack_group_memberships || []).map(
            (member) => member.variant_pack_group_uuid,
          ),
          parentVariantPackGroupMap,
        );

        if (isInSelectedCollection && ogItemGroupUUIDs.length > 0) {
          ogItemGroupUUIDs.forEach((groupUUID) => {
            const variantPackGroup = variantPackGroupMap[groupUUID];
            if (variantPackGroup) {
              acc.add(variantPackGroup);
            }
          });
        }

        return acc;
      },
      new Set(),
    );
    return itemGroups.size > 1
      ? Array.from(itemGroups).sort(
          (i1: VariantPackGroup, i2: VariantPackGroup) =>
            i1.name.localeCompare(i2.name),
        )
      : [];
  }, [
    parentVariantPackGroupMap,
    selectedCollection,
    storeOrderGuideItems,
    supplierOrderGuideCollectionsMap,
    variantPackGroupMap,
  ]);

  // if any of the categories in the filter are not in the categories array
  // the OG no longer includes that category. So remove that category from the filter
  useEffect(() => {
    setCategoryFilter((prevFilter) => {
      if (!showCategoryFilters) {
        // If filter selector is hidden, clear filter to reset
        return null;
      }
      if (prevFilter && !categories.includes(prevFilter)) {
        return null;
      }
      return prevFilter;
    });
  }, [categories, showCategoryFilters]);

  // if any of the groups in the filter are not in the groups array
  // the OG no longer includes an item in that group. So remove that group from the filter
  useEffect(() => {
    setCategoryItemGroupFilter((prevFilter) => {
      const itemGroupUUIDSet = categoryItemGroups.reduce<Set<string>>(
        (acc: Set<string>, group: VariantPackGroup) => {
          acc.add(group.uuid);
          return acc;
        },
        new Set(),
      );
      if (prevFilter?.uuid && !itemGroupUUIDSet.has(prevFilter.uuid)) {
        return null;
      }
      return prevFilter;
    });
  }, [categoryItemGroups]);

  const [currencyCode, partitionFilters, sections] = useMemo(() => {
    const defaultCollectionPartitionUUID = selectedCollection?.isDefault
      ? selectedCollection.defaultPartitionUUID
      : null;

    const categoryItemGroupFilterWithSubcategories =
      categoryItemGroupFilter?.uuid
        ? [
            categoryItemGroupFilter.uuid,
            ...(childVariantPackGroupMap[categoryItemGroupFilter.uuid] || []),
          ]
        : [];

    // Create a map of linked supplier -> restaurant partitions so items from
    // the linked supplier collection are included in the list
    const supplierToRestaurantPartitionMap: Record<string, string> = {};
    if (selectedCollection?.linkedSupplierCollectionUUID) {
      const defaultRestaurantPartitionUUID =
        selectedCollection.defaultPartitionUUID;
      const defaultSupplierPartitionUUID = supplierOrderGuideCollectionsMap[
        selectedCollection.linkedSupplierCollectionUUID
      ]?.order_guide_supplier_partitions.find((ogsp) => ogsp.is_default)?.uuid;
      if (defaultRestaurantPartitionUUID && defaultSupplierPartitionUUID) {
        supplierToRestaurantPartitionMap[defaultSupplierPartitionUUID] =
          defaultRestaurantPartitionUUID;
      }
    }

    // Construct a map of partition to its order guide items so variant packs
    // can be grouped within the same partition
    const partitionOrderGuideItemsMap = storeOrderGuideItems.reduce<{
      [partitionUUID: string]: ItemsContext_OrderGuideItemFieldsFragment[];
    }>((acc, ogi) => {
      // add OGR to its partition, or OGS to the default collection partition
      // Do not add OGS to non-default collection
      let partitionUUID =
        ogi.order_guide_item_partition_id || defaultCollectionPartitionUUID;
      if (partitionUUID && ogi.variant_pack) {
        partitionUUID =
          supplierToRestaurantPartitionMap[partitionUUID] || partitionUUID;

        if (!acc[partitionUUID]) {
          acc[partitionUUID] = [];
        }

        acc[partitionUUID].push(ogi);
      }

      return acc;
    }, {});

    let nextCurrencyCode: string | null = null;
    const nextPartitionFilters: PartitionFilterType[] = [];
    const nextSections: ItemListOptionSection[] = [];

    // Partitions should already be in sort order
    const partitions = selectedCollection?.partitions || [];
    for (const partition of partitions) {
      const partitionOrderGuideItems =
        partitionOrderGuideItemsMap[partition.uuid] || [];

      // Include non-default partitions or the default partition if it has items
      if (!partition.isDefault || partitionOrderGuideItems.length > 0) {
        nextPartitionFilters.push({
          metadata: {
            isDefault: partition.isDefault,
          },
          title: partition.name,
          value: partition.uuid,
        });

        const itemListOptions = groupItemListOptions(
          partitionOrderGuideItems.reduce<ItemListOption[]>((acc, ogi) => {
            const adaptedItem = adaptVariantPackToItemListOption(
              // partitionOrderGuideItems only includes items with variant_pack
              ogi.variant_pack!,
            );
            const adaptedItemCurrencyCode = adaptedItem.metadata?.currencyCode;
            if (adaptedItemCurrencyCode && !nextCurrencyCode) {
              nextCurrencyCode = adaptedItemCurrencyCode;
            }

            const itemCategory = adaptedItem.metadata?.category;
            const itemCategoryVariantPackGroupUUIDs =
              adaptedItem.metadata?.variantPackGroupCategoryUUIDs || [];

            const setOrderGuidePartitionUUID = itemGroupsEnabled
              ? !categoryItemGroupFilter ||
                !categoryItemGroupFilter?.uuid ||
                itemCategoryVariantPackGroupUUIDs.some((groupUUID) =>
                  categoryItemGroupFilterWithSubcategories.includes(groupUUID),
                )
              : !categoryFilter ||
                (itemCategory && itemCategory === categoryFilter);

            if (setOrderGuidePartitionUUID) {
              // Set orderGuidePartitionUUID so it can be used to discern the
              // same variant pack rendering in different partitions and for
              // rendering order guide info
              adaptedItem.metadata!.orderGuidePartitionUUID =
                ogi.order_guide_item_partition_id || partition.uuid;
              acc.push(adaptedItem);
            }

            return acc;
          }, []),
          itemDisplayOptions,
        );

        nextSections.push({
          data: sortItemListOptions(itemListOptions, orderGuideSortOrder),
          key: partition.name,
        });
      }
    }

    return [
      nextCurrencyCode || DEFAULT_CURRENCY_CODE,
      nextPartitionFilters,
      nextSections,
    ];
  }, [
    adaptVariantPackToItemListOption,
    categoryFilter,
    categoryItemGroupFilter,
    childVariantPackGroupMap,
    itemDisplayOptions,
    itemGroupsEnabled,
    orderGuideSortOrder,
    selectedCollection,
    storeOrderGuideItems,
    supplierOrderGuideCollectionsMap,
  ]);

  // if any of the partitions in the filter are not in the partitions array
  // the OG no longer includes that partition. So reset the filter
  useEffect(() => {
    if (
      partitionFilter &&
      !partitionFilters.some((filter) => filter.value === partitionFilter.value)
    ) {
      setPartitionFilter(null);
    }
  }, [partitionFilter, partitionFilters]);

  const variantPackPartitionMap = useMemo(() => {
    return storeOrderGuideItems.reduce<VariantPackPartitionMap>((acc, ogi) => {
      const partitionUUID =
        ogi.order_guide_item_partition_id ||
        selectedCollection?.defaultPartitionUUID;
      const variantPackUUID = ogi.variant_pack?.uuid;
      if (partitionUUID && variantPackUUID) {
        if (!acc[variantPackUUID]) {
          acc[variantPackUUID] = {};
        }

        acc[variantPackUUID][partitionUUID] = ogi;
      }

      return acc;
    }, {});
  }, [selectedCollection, storeOrderGuideItems]);

  const partitionToCollectionMap = useMemo(
    () => buildPartitionToCollectionMap(collections),
    [collections],
  );

  const refetch = queryResult.refetch;
  const itemsContextValue = useMemo(() => {
    return {
      allVariantPacks,
      categories,
      categoryFilter,
      categoryItemGroupFilter,
      categoryItemGroups,
      collections,
      currencyCode,
      hasResponse: storeIsHydrated,
      isInOrderGuide: (variantPackUUID: string) => {
        return selectIsInOrderGuide(
          orderGuideStoreApi.getState(),
          variantPackUUID,
        );
      },
      loading: queryResult.loading,
      partitionFilter,
      partitionFilters,
      partitionToCollectionMap,
      refetch: async () => {
        await refetch();
      },
      sections: partitionFilter
        ? sections.filter((s) => s.key === partitionFilter.title)
        : sections,
      selectedCollection,
      setCategoryFilter,
      setCategoryItemGroupFilter,
      setPartitionFilter,
      variantPackPartitionMap,
    };
  }, [
    allVariantPacks,
    categories,
    categoryFilter,
    categoryItemGroupFilter,
    categoryItemGroups,
    collections,
    currencyCode,
    orderGuideStoreApi,
    partitionFilter,
    partitionFilters,
    partitionToCollectionMap,
    storeIsHydrated,
    queryResult.loading,
    refetch,
    sections,
    selectedCollection,
    variantPackPartitionMap,
  ]);

  return (
    <OrderGuideItemsContext.Provider value={itemsContextValue}>
      {children}
    </OrderGuideItemsContext.Provider>
  );
};
